import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Container,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { EventAvailable } from '@mui/icons-material';
import CalendarService from '../../services/calendar.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAbsoluteUrl } from '../../utils/assetUrl';

const PublicCalendar = () => {
  const { t, language } = useTranslation();
  const { setLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const locale = language === 'es' ? 'es-ES' : 'en-US';
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [daySchedule, setDaySchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openBook, setOpenBook] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [booking, setBooking] = useState(false);
  const [tenantLabel, setTenantLabel] = useState('');
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const [form, setForm] = useState({
    new_patient_name: '',
    new_patient_email: '',
    new_patient_phone: '',
  });

  const tenantHeaders = useCallback(() => {
    const h = {};
    const tid = user?.tenant_id;
    if (isAuthenticated && tid) {
      h['X-Tenant-ID'] = String(tid);
    }
    return h;
  }, [isAuthenticated, user?.tenant_id]);

  const availableSlots = useMemo(
    () => (daySchedule?.time_slots || []).filter((slot) => slot.is_available),
    [daySchedule]
  );

  const loadTenantInfo = useCallback(async () => {
    try {
      const res = await CalendarService.getPublicTenantInfo(tenantHeaders());
      const name = res.data?.name;
      if (name) setTenantLabel(name);
      setTenantLogoUrl(res.data?.logo_url || null);
      const lang = res.data?.public_calendar_language === 'es' ? 'es' : 'en';
      return lang;
    } catch {
      return null;
    }
  }, [tenantHeaders]);

  const loadDay = useCallback(
    async (date) => {
      try {
        setLoading(true);
        setError('');
        const response = await CalendarService.getPublicDaySchedule(date, tenantHeaders());
        setDaySchedule(response.data);
      } catch (err) {
        if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
          setError(t('calendar.public.networkError'));
          setDaySchedule(null);
          return;
        }
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          t('calendar.public.loadError');
        setError(typeof msg === 'string' ? msg : t('calendar.public.loadError'));
        setDaySchedule(null);
      } finally {
        setLoading(false);
      }
    },
    [tenantHeaders, t]
  );

  useEffect(() => {
    const previousLanguage = localStorage.getItem('app-language') || 'en';
    let cancelled = false;

    (async () => {
      const lang = await loadTenantInfo();
      if (!cancelled && lang) {
        setLanguage(lang);
      }
    })();

    return () => {
      cancelled = true;
      setLanguage(previousLanguage);
    };
  }, [loadTenantInfo, setLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    loadDay(selectedDate);
  }, [selectedDate, loadDay]);

  const openBookForSlot = (slot) => {
    setSelectedSlot(slot);
    setError('');
    setSuccess('');
    setOpenBook(true);
  };

  const handleSubmitBooking = async () => {
    if (!selectedSlot) return;
    if (!String(form.new_patient_name || '').trim()) {
      setError(t('calendar.public.nameRequired'));
      return;
    }

    try {
      setBooking(true);
      setError('');
      await CalendarService.bookPublicAppointment(
        {
          date: selectedDate,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          new_patient_name: form.new_patient_name.trim(),
          new_patient_email: form.new_patient_email?.trim() || null,
          new_patient_phone: form.new_patient_phone?.trim() || null,
          save_as_new_client: false,
        },
        tenantHeaders()
      );
      setSuccess(t('calendar.public.bookingSuccess'));
      setOpenBook(false);
      setForm({ new_patient_name: '', new_patient_email: '', new_patient_phone: '' });
      await loadDay(selectedDate);
    } catch (err) {
      if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
        setError(t('calendar.public.networkError'));
      } else {
        setError(err?.response?.data?.message || t('calendar.public.bookingError'));
      }
    } finally {
      setBooking(false);
    }
  };

  const pageTitle = tenantLabel
    ? t('calendar.public.titleWithDoctor', { name: tenantLabel })
    : t('calendar.public.titleDefault');

  const tenantLogoSrc = resolveAbsoluteUrl(tenantLogoUrl);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #e8eaf6 0%, #f3e5f5 40%, #e3f2fd 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              px: 3,
              py: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={language}
                onChange={(e, value) => value && setLanguage(value)}
                aria-label="language"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    border: 'none',
                    px: 1.5,
                    py: 0.25,
                    fontWeight: 600,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: '#764ba2',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                    },
                  },
                }}
              >
                <ToggleButton value="en" aria-label="English">EN</ToggleButton>
                <ToggleButton value="es" aria-label="Español">ES</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              {tenantLogoSrc ? (
                <Box
                  component="img"
                  src={tenantLogoSrc}
                  alt={tenantLabel || t('calendar.public.titleDefault')}
                  sx={{
                    width: 52,
                    height: 52,
                    objectFit: 'contain',
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    p: 0.5,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <EventAvailable sx={{ fontSize: 36, opacity: 0.95, flexShrink: 0 }} />
              )}
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {pageTitle}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 520 }}>
              {t('calendar.public.subtitle')}
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <TextField
              label={t('calendar.public.selectDate')}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ lang: locale }}
              fullWidth
              sx={{ mb: 3, maxWidth: 320 }}
            />

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && !daySchedule?.is_working_day && (
              <Alert severity="info">{t('calendar.public.offDay')}</Alert>
            )}

            {!loading && daySchedule?.is_working_day && (
              <Grid container spacing={2}>
                {availableSlots.map((slot, index) => (
                  <Grid item xs={12} sm={6} key={`${slot.start_time}-${index}`}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 2 },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {slot.start_time} – {slot.end_time}
                      </Typography>
                      <Button variant="contained" fullWidth onClick={() => openBookForSlot(slot)}>
                        {t('calendar.public.bookThisSlot')}
                      </Button>
                    </Paper>
                  </Grid>
                ))}
                {availableSlots.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning">{t('calendar.public.noSlots')}</Alert>
                  </Grid>
                )}
              </Grid>
            )}

            {!isAuthenticated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                {t('calendar.public.tenantHint')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>

      <Dialog open={openBook} onClose={() => setOpenBook(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('calendar.public.bookDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {selectedDate} · {selectedSlot?.start_time} – {selectedSlot?.end_time}
          </Typography>
          <TextField
            fullWidth
            required
            label={t('calendar.public.newPatientName')}
            value={form.new_patient_name}
            onChange={(e) => setForm((prev) => ({ ...prev, new_patient_name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="email"
            label={t('calendar.public.newPatientEmail')}
            value={form.new_patient_email}
            onChange={(e) => setForm((prev) => ({ ...prev, new_patient_email: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="tel"
            label={t('calendar.public.newPatientPhone')}
            value={form.new_patient_phone}
            onChange={(e) => setForm((prev) => ({ ...prev, new_patient_phone: e.target.value }))}
            helperText={t('calendar.public.contactHint')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenBook(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmitBooking} disabled={booking}>
            {booking ? t('common.saving') : t('calendar.public.confirmBooking')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicCalendar;
